// src/components/leaderboard/LeaderboardPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Search, Award, ChevronDown } from "lucide-react";
import {
  getMockAchievementUsers,
  ACHIEVEMENTS,
  INDIAN_STATES,
} from "@/utils/constants";
import Table from "../common/Table";
import SelectDropdown from "../common/SelectDropdown";

const LeaderboardPage = () => {
  const [selectedAchievement, setSelectedAchievement] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [users, setUsers] = useState([]);

  // Fetch users when achievement changes
  useEffect(() => {
    if (selectedAchievement) {
      const data = getMockAchievementUsers(selectedAchievement);
      setUsers(data);
    } else {
      setUsers([]);
    }
  }, [selectedAchievement]);

  // Filter users based on search and state
  const filteredUsers = useMemo(() => {
    return users.filter((user) => {
      const matchesSearch =
        !searchQuery ||
        user.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.name.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesState = !stateFilter || user.state === stateFilter;

      return matchesSearch && matchesState;
    });
  }, [users, searchQuery, stateFilter]);

  // Get state options for filter dropdown
  const stateOptions = useMemo(() => {
    return [
      { value: "", label: "All States" },
      ...INDIAN_STATES.filter((s) => s.code).map((s) => ({
        value: s.code,
        label: s.name,
      })),
    ];
  }, []);

  // Table columns
  const columns = [
    { key: "username", title: "Username" },
    { key: "name", title: "Name" },
    { key: "location", title: "Location" },
  ];

  // Render table row
  const renderRow = (user) => (
    <tr
      key={user.id}
      className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
    >
      <td className="px-4 py-3 text-gray-900 dark:text-gray-200">
        <span className="font-medium">@{user.username}</span>
      </td>
      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
        {user.name}
      </td>
      <td className="px-4 py-3 text-gray-500 dark:text-gray-400">
        {user.location_display}
      </td>
    </tr>
  );

  return (
    <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8 min-h-screen">
      {/* Page Header */}
      <div className="text-center mb-12">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-emerald-400 to-teal-600 mb-4 shadow-lg shadow-emerald-500/30">
          <Award className="w-8 h-8 text-white" />
        </div>
        <h1 className="text-3xl md:text-4xl font-bold dark:text-white transition-colors duration-300 mb-2">
          Leaderboard
        </h1>
        <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto">
          Celebrating riders who have completed special achievements
        </p>
      </div>

      {/* Achievement Selector - Prominent Dropdown */}
      <div className="max-w-md mx-auto mb-8 sm:mb-10 px-2 sm:px-0">
        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 text-center">
          Select an Achievement to View
        </label>
        <div className="achievement-dropdown-wrapper">
          <SelectDropdown
            options={ACHIEVEMENTS}
            value={selectedAchievement}
            onChange={(e) => setSelectedAchievement(e.target.value)}
            placeholder="Choose an achievement..."
            id="achievement-select"
            className="!mb-0 achievement-dropdown"
          />
        </div>
      </div>

      {/* Results Section - Only show when achievement is selected */}
      {selectedAchievement && (
        <div className="animate-fadeIn">
          {/* Filters Row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-6 items-stretch">
            {/* Search Bar */}
            <div className="sm:col-span-2 lg:col-span-2">
              <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-xl px-4 py-3 h-[46px] border border-gray-200 dark:border-gray-600 focus-within:ring-2 focus-within:ring-emerald-500/50 focus-within:border-emerald-500 transition-all">
                <Search className="w-5 h-5 text-gray-500 dark:text-gray-400 mr-3 flex-shrink-0" />
                <input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by username or name..."
                  className="bg-transparent outline-none text-sm text-gray-700 dark:text-gray-200 w-full placeholder-gray-400 dark:placeholder-gray-500"
                />
              </div>
            </div>

            {/* State Filter */}
            <div className="sm:col-span-2 lg:col-span-1">
              <SelectDropdown
                options={stateOptions}
                value={stateFilter}
                onChange={(e) => setStateFilter(e.target.value)}
                placeholder="Filter by state..."
                id="state-filter"
                className="!mb-0 state-filter-dropdown"
              />
            </div>
          </div>

          {/* Results Count */}
          <div className="mb-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Showing{" "}
              <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                {filteredUsers.length}
              </span>{" "}
              {filteredUsers.length === 1 ? "rider" : "riders"} who completed{" "}
              <span className="font-semibold">
                {
                  ACHIEVEMENTS.find((a) => a.value === selectedAchievement)
                    ?.label
                }
              </span>
            </p>
          </div>

          {/* Users Table */}
          {filteredUsers.length > 0 ? (
            <Table columns={columns} data={filteredUsers} renderRow={renderRow} />
          ) : (
            <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-2xl border border-gray-200 dark:border-gray-700">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-200 dark:bg-gray-700 mb-4">
                <Search className="w-6 h-6 text-gray-400" />
              </div>
              <p className="text-gray-500 dark:text-gray-400">
                No riders found matching your filters.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Empty State - When no achievement selected */}
      {!selectedAchievement && (
        <div className="text-center py-16">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 mb-6">
            <ChevronDown className="w-10 h-10 text-gray-400 dark:text-gray-500 animate-bounce" />
          </div>
          <p className="text-gray-500 dark:text-gray-400 text-lg">
            Select an achievement above to see the riders who completed it
          </p>
        </div>
      )}

      {/* Custom Styles */}
      <style>{`
        .achievement-dropdown-wrapper .custom-dropdown {
          max-width: 100% !important;
        }

        .achievement-dropdown-wrapper .dropdown-button {
          padding: 14px 18px !important;
          font-size: 1rem !important;
          border-radius: 14px !important;
          border-width: 2px !important;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
        }

        .achievement-dropdown-wrapper .dropdown-button:hover {
          border-color: #10b981 !important;
        }

        .achievement-dropdown-wrapper .dropdown-popover {
          border-radius: 14px !important;
          border-width: 2px !important;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
        }

        .achievement-dropdown-wrapper .dropdown-item {
          padding: 12px 18px !important;
          font-size: 1rem !important;
        }

        /* State filter alignment fix */
        .state-filter-dropdown.custom-dropdown,
        .state-filter-dropdown .custom-dropdown {
          max-width: 100% !important;
        }

        .state-filter-dropdown .dropdown-button {
          height: 46px !important;
          display: flex !important;
          align-items: center !important;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out forwards;
        }

        /* Mobile responsiveness */
        @media (max-width: 640px) {
          .achievement-dropdown-wrapper .dropdown-button {
            padding: 12px 16px !important;
            font-size: 0.95rem !important;
          }
          
          .achievement-dropdown-wrapper .dropdown-item {
            padding: 10px 16px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default LeaderboardPage;
